import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiCheck, FiX, FiCreditCard, FiZap, FiShield, FiBarChart, FiLock, FiRefreshCw } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';

const UpgradePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isGuest } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [subscription, setSubscription] = useState<any>(null);

  useEffect(() => {
    // Redirect guests to register
    if (isGuest) {
      navigate('/register');
      toast('Please create an account to upgrade', { icon: '👤' });
      return;
    }

    // Load subscription status
    loadSubscription();
  }, [isGuest, navigate]);

  const loadSubscription = async () => {
    try {
      const response = await api.getSubscriptionStatus();
      setSubscription(response.data);
    } catch (error) {
      console.error('Failed to load subscription:', error);
    }
  };

  const handleUpgrade = async () => {
    setIsLoading(true);
    try {
      const response = await api.createCheckoutSession({
        successUrl: `${window.location.origin}/dashboard?upgraded=true`,
        cancelUrl: `${window.location.origin}/upgrade`
      });

      // Redirect to Stripe checkout
      window.location.href = response.data.checkoutUrl;
    } catch (error: any) {
      toast.error('Failed to start upgrade process');
      setIsLoading(false);
    }
  };

  const handleManageBilling = async () => {
    setIsLoading(true);
    try {
      const response = await api.createBillingPortalSession(
        `${window.location.origin}/account`
      );

      // Redirect to Stripe billing portal
      window.location.href = response.data.portalUrl;
    } catch (error: any) {
      toast.error('Failed to open billing portal');
      setIsLoading(false);
    }
  };

  const features = {
    free: [
      { text: '10 links per month', included: true },
      { text: 'Links expire in max 30 days', included: true },
      { text: 'Basic analytics', included: true },
      { text: 'QR codes', included: true },
      { text: 'Standard support', included: true },
      { text: 'Custom aliases', included: false },
      { text: 'Permanent links', included: false },
      { text: 'API access', included: false },
      { text: 'Bulk operations', included: false },
      { text: 'Priority support', included: false }
    ],
    pro: [
      { text: 'Unlimited links', included: true },
      { text: 'Links that never expire', included: true },
      { text: 'Custom aliases', included: true },
      { text: 'QR codes', included: true },
      { text: 'Advanced analytics', included: true },
      { text: 'API access', included: true },
      { text: 'Bulk operations', included: true },
      { text: 'Priority support', included: true }
    ]
  };

  if (isGuest) {
    return null; // Will redirect
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Choose Your Plan
          </h1>
          <p className="text-xl text-gray-600">
            Upgrade to Pro for unlimited links and premium features
          </p>
        </div>

        {/* Current Plan Indicator */}
        {user && (
          <div className="max-w-4xl mx-auto mb-8">
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <FiCreditCard className="w-5 h-5 text-gray-600" />
                  <span className="text-gray-700">Current Plan:</span>
                  <span className="font-semibold text-primary-600 capitalize">
                    {user.tier === 'pro' ? 'Pro' : 'Free'} Plan
                  </span>
                </div>
                {user.tier === 'pro' && subscription && (
                  <button
                    onClick={handleManageBilling}
                    disabled={isLoading}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Manage Billing
                  </button>
                )}
              </div>
              {subscription?.endsAt && (
                <p className="text-sm text-gray-500 mt-2 ml-8">
                  {subscription.status === 'canceling' 
                    ? `Cancels on ${new Date(subscription.endsAt).toLocaleDateString()}`
                    : `Renews on ${new Date(subscription.endsAt).toLocaleDateString()}`
                  }
                </p>
              )}
            </div>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Free Plan */}
            <div className={`bg-white rounded-2xl shadow-lg overflow-hidden ${user?.tier === 'free' ? 'ring-2 ring-primary-500' : ''}`}>
              {user?.tier === 'free' && (
                <div className="bg-primary-500 text-white text-center py-2 text-sm font-medium">
                  Current Plan
                </div>
              )}
              <div className="p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Free</h2>
                <div className="flex items-baseline mb-6">
                  <span className="text-4xl font-bold">$0</span>
                  <span className="text-gray-500 ml-2">/month</span>
                </div>
                <p className="text-gray-600 mb-6">
                  Perfect for personal use and trying out AnonLink
                </p>

                {/* Features */}
                <ul className="space-y-3 mb-8">
                  {features.free.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      {feature.included ? (
                        <FiCheck className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                      ) : (
                        <FiX className="w-5 h-5 text-gray-300 mr-3 flex-shrink-0 mt-0.5" />
                      )}
                      <span className={feature.included ? 'text-gray-700' : 'text-gray-400'}>
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>

                {user?.tier === 'free' ? (
                  <button
                    disabled
                    className="w-full py-3 px-4 bg-gray-100 text-gray-500 rounded-lg font-medium cursor-not-allowed"
                  >
                    Current Plan
                  </button>
                ) : (
                  <button
                    onClick={() => navigate('/register')}
                    className="w-full py-3 px-4 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                  >
                    Get Started
                  </button>
                )}
              </div>
            </div>

            {/* Pro Plan */}
            <div className={`bg-white rounded-2xl shadow-lg overflow-hidden relative ${user?.tier === 'pro' ? 'ring-2 ring-primary-500' : ''}`}>
              {/* Popular badge */}
              {user?.tier !== 'pro' && (
                <div className="absolute top-0 right-0 bg-gradient-to-r from-primary-500 to-primary-600 text-white px-4 py-1 rounded-bl-lg text-sm font-medium">
                  Most Popular
                </div>
              )}
              {user?.tier === 'pro' && (
                <div className="bg-primary-500 text-white text-center py-2 text-sm font-medium">
                  Current Plan
                </div>
              )}
              <div className="p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Pro</h2>
                <div className="flex items-baseline mb-6">
                  <span className="text-4xl font-bold">$2.99</span>
                  <span className="text-gray-500 ml-2">/month</span>
                </div>
                <p className="text-gray-600 mb-6">
                  For power users who need unlimited links and advanced features
                </p>

                {/* Features */}
                <ul className="space-y-3 mb-8">
                  {features.pro.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <FiCheck className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>

                {user?.tier === 'pro' ? (
                  <button
                    onClick={handleManageBilling}
                    disabled={isLoading}
                    className="w-full py-3 px-4 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
                  >
                    {isLoading ? 'Loading...' : 'Manage Subscription'}
                  </button>
                ) : (
                  <button
                    onClick={handleUpgrade}
                    disabled={isLoading}
                    className="w-full py-3 px-4 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors flex items-center justify-center"
                  >
                    {isLoading ? (
                      'Loading...'
                    ) : (
                      <>
                        <FiZap className="w-5 h-5 mr-2" />
                        Upgrade to Pro
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Additional Benefits */}
        <div className="max-w-4xl mx-auto mt-16">
          <h3 className="text-2xl font-bold text-center text-gray-900 mb-8">
            Why Upgrade to Pro?
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <FiRefreshCw className="w-6 h-6 text-primary-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Unlimited Links</h4>
              <p className="text-sm text-gray-600">
                Create as many short links as you need without any monthly limits
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <FiBarChart className="w-6 h-6 text-primary-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Advanced Analytics</h4>
              <p className="text-sm text-gray-600">
                Get detailed insights into your link performance and audience
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <FiShield className="w-6 h-6 text-primary-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Priority Support</h4>
              <p className="text-sm text-gray-600">
                Get help faster with priority customer support
              </p>
            </div>
          </div>
        </div>

        {/* FAQ or Trust badges */}
        <div className="max-w-2xl mx-auto mt-16 text-center">
          <div className="flex items-center justify-center space-x-8 text-gray-500">
            <div className="flex items-center">
              <FiLock className="w-5 h-5 mr-2" />
              <span className="text-sm">Secure Payment</span>
            </div>
            <div className="flex items-center">
              <FiShield className="w-5 h-5 mr-2" />
              <span className="text-sm">Cancel Anytime</span>
            </div>
            <div className="flex items-center">
              <FiCreditCard className="w-5 h-5 mr-2" />
              <span className="text-sm">Powered by Stripe</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpgradePage;